import { AfterViewInit, Directive, ElementRef, HostListener, inject, input, Renderer2 } from '@angular/core';
import { CapturumAutocompleteComponent } from '@capturum/ui/autocomplete';
import { CapturumInputComponent } from '@capturum/ui/input';

@Directive({
  selector: 'input[appInputPrefix],cap-input[appInputPrefix],cap-autocomplete[appInputPrefix]',
  standalone: true,
})
export class InputPrefixDirective implements AfterViewInit {
  prefix = input('');
  prefixDisabled = input(false);

  #elementRef = inject<ElementRef<HTMLInputElement>>(ElementRef<HTMLInputElement>);
  #renderer = inject(Renderer2);
  #capInput = inject(CapturumInputComponent, { optional: true });
  #capAutocomplete = inject(CapturumAutocompleteComponent, { optional: true });

  /**
   * @description
   * For now this directive is used to add a prefix to an input field, cap-input or cap-autocomplete.
   */
  #input: ElementRef<HTMLInputElement>['nativeElement'] | CapturumInputComponent | CapturumAutocompleteComponent;

  get #nativeInput(): HTMLInputElement | null {
    return this.#elementRef.nativeElement;
  }

  @HostListener('input', ['$event'])
  onInput(event: InputEvent): void {
    if (this.prefixDisabled()) {
      return;
    }

    const input = this.#getElementInput();

    if (!input.value.startsWith(this.prefix())) {
      const cursorPos = input.selectionStart ?? this.prefix().length;

      this.#renderer.setProperty(input, 'value', this.prefix());
      input.setSelectionRange(this.prefix().length, this.prefix().length);
      event.preventDefault?.();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.prefixDisabled()) {
      return;
    }

    const input = this.#getElementInput();
    const cursorPos = input.selectionStart ?? 0;

    // Prevent deleting or moving before prefix
    if (cursorPos <= this.prefix().length && (event.key === 'Backspace' || event.key === 'ArrowLeft')) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    if (this.prefixDisabled()) {
      return;
    }

    const input = this.#getElementInput();
    const pasteText = event.clipboardData?.getData('text') ?? '';

    // Prevent pasting before the prefix
    const cursorPos = input.selectionStart ?? 0;

    if (cursorPos < this.prefix().length) {
      event.preventDefault();
    } else {
      // Paste after prefix and set new value
      const before = input.value.slice(0, cursorPos);
      const after = input.value.slice(cursorPos);
      const newVal = before + pasteText + after;

      if (!newVal.startsWith(this.prefix())) {
        event.preventDefault();
      }

      this.#renderer.setProperty(input, 'value', newVal);
      input.setSelectionRange(cursorPos + pasteText.length, cursorPos + pasteText.length);
    }
  }

  ngAfterViewInit(): void {
    if (this.prefixDisabled()) {
      return;
    }

    this.#input =
      this.#elementRef.nativeElement.tagName.toLowerCase() === 'cap-input'
        ? this.#capInput
        : this.#elementRef.nativeElement.tagName.toLowerCase() === 'cap-autocomplete'
          ? this.#capAutocomplete
          : this.#nativeInput;

    if (!this.#input?.value?.startsWith(this.prefix())) {
      if (this.#input instanceof HTMLInputElement) {
        this.#renderer.setProperty(this.#input, 'value', this.prefix());
      } else {
        this.#input.value = this.prefix();
      }
    }
  }

  #getElementInput(): HTMLInputElement {
    const input =
      this.#input instanceof HTMLInputElement ? this.#input : this.#elementRef.nativeElement.querySelector('input');

    return input;
  }
}
